import React from 'react'

import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

export const SWITCH_CSS_VARIABLES = {
  trackColor: '--switch-track-color',
  checkedTrackColor: '--switch-checked-track-color',
  thumbColor: '--switch-thumb-color',
  checkedThumbColor: '--switch-checked-thumb-color',
  hoverRingColor: '--switch-hover-ring-color',
  checkedHoverRingColor: '--switch-checked-hover-ring-color',
  disabledColor: '--switch-disabled-color',
  baseBackgroundColor: '--switch-base-background-color',
}

const Thumb = styled.span`
  box-shadow: ${({ theme }) => theme.shadows[10]};
  background-color: var(--thumb-color);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  transition: var(--background-color-transition);
`

const ThumbWrapper = styled.span`
  width: 100%;
  display: flex;
  align-items: inherit;
  justify-content: inherit;

  svg {
    flex-shrink: 0;
  }
`

const Track = styled.span`
  height: 100%;
  width: 100%;
  z-index: -1;
  transition:
    opacity var(--transition-details),
    background-color var(--transition-details);
  background-color: var(--track-color);
  opacity: 0.38;
  border-radius: 7px;
`

const Base = styled.span`
  top: 0;
  left: 0;
  bottom: 0;
  margin: auto;
  z-index: 1;
  position: absolute;
  transition:
    left var(--transition-details),
    transform var(--transition-details),
    background-color var(--transition-details);
  padding: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  user-select: none;
  justify-content: center;
  vertical-align: middle;

  &[data-checked='true'] {
    transform: translateX(20px);
  }

  &[data-checked='true'] + ${Track} {
    opacity: 0.5;
  }

  &[aria-disabled='true'] + ${Track} {
    opacity: 0.12;
  }
`

const Wrapper = styled.span`
  --thumb-color: var(${SWITCH_CSS_VARIABLES.thumbColor}, ${({ theme }) => theme.colors.gray[10]});
  --checked-thumb-color: var(${SWITCH_CSS_VARIABLES.checkedThumbColor}, ${({ theme }) => theme.colors.primary[50]});
  --disabled-thumb-color: var(${SWITCH_CSS_VARIABLES.disabledColor}, ${({ theme }) => theme.colors.gray[130]});
  --track-color: var(${SWITCH_CSS_VARIABLES.trackColor}, ${({ theme }) => theme.colors.gray[100]});
  --checked-track-color: var(${SWITCH_CSS_VARIABLES.checkedTrackColor}, ${({ theme }) => theme.colors.primary[50]});
  --disabled-track-color: var(${SWITCH_CSS_VARIABLES.disabledColor}, ${({ theme }) => theme.colors.gray[100]});
  --hover-ring-color: var(
    ${SWITCH_CSS_VARIABLES.hoverRingColor},
    ${({ theme }) => applyOpacity(theme.colors.gray[100], 4)}
  );
  --checked-hover-ring-color: var(
    ${SWITCH_CSS_VARIABLES.checkedHoverRingColor},
    ${({ theme }) => applyOpacity(theme.colors.primary[50], 4)}
  );
  --base-background-color: var(${SWITCH_CSS_VARIABLES.baseBackgroundColor}, unset);
  --transition-details: 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  width: 58px;
  height: 38px;
  display: inline-flex;
  padding: 12px;
  z-index: 0;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  vertical-align: middle;
  color: var(--thumb-color);

  input {
    width: 300%;
    height: 100%;
    margin: 0;
    top: 0;
    cursor: inherit;
    height: 100%;
    opacity: 0;
    z-index: 1;
    position: absolute;
  }

  ${Base} {
    background-color: var(--base-background-color);

    &:hover {
      background-color: var(--hover-ring-color);
    }
  }

  &:has([data-checked='true']) {
    --thumb-color: var(--checked-thumb-color);
    --track-color: var(--checked-track-color);
    --hover-ring-color: var(--checked-hover-ring-color);
  }

  &:has([aria-disabled='true']) {
    --thumb-color: var(--disabled-thumb-color);
    --track-color: var(--disabled-track-color);
  }
`

export interface SwitchProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean
  checkedIcon?: React.ReactNode
  className?: string
  disabled?: boolean
  icon?: React.ReactNode
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => void
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked = false, className, disabled = false, icon = <Thumb />, checkedIcon = icon, onChange, ...rest }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event, event.target.checked)
    }

    return (
      <Wrapper className={className}>
        <Base aria-disabled={disabled} data-checked={checked}>
          <ThumbWrapper>
            <input
              disabled={disabled}
              onChange={handleChange}
              ref={ref}
              type="checkbox"
              // If onChange is provided, the component is controlled, otherwise it's uncontrolled
              {...(onChange != null ? { checked } : { defaultChecked: checked })}
              {...rest}
            />
            {checked ? checkedIcon : icon}
          </ThumbWrapper>
        </Base>
        <Track />
      </Wrapper>
    )
  }
)

Switch.displayName = 'Switch'
